import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo_url?: string;
  plan_type: string;
  is_active: boolean;
  settings: any;
  billing_email?: string;
  contact_email?: string;
  website_url?: string;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: string;
  invited_by?: string;
  invited_at?: string;
  joined_at?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  organization?: Organization;
}

export function useOrganizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [userMemberships, setUserMemberships] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const fetchUserOrganizations = async () => {
    try {
      setLoading(true);
      
      // Récupérer les organisations où l'utilisateur est membre
      const { data: memberships, error } = await supabase
        .from('organization_members')
        .select(`
          *,
          organization:organizations(*)
        `)
        .eq('is_active', true);

      if (error) throw error;

      setUserMemberships(memberships || []);
      const orgs = memberships?.map(m => m.organization).filter(Boolean) || [];
      setOrganizations(orgs);

      // Récupérer l'organisation actuelle du profil utilisateur
      const { data: profile } = await supabase
        .from('profiles')
        .select('current_organization_id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (profile?.current_organization_id) {
        const currentOrg = orgs.find(org => org.id === profile.current_organization_id);
        setCurrentOrganization(currentOrg || null);
      }

    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les organisations"
      });
    } finally {
      setLoading(false);
    }
  };

  const switchOrganization = async (organizationId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ current_organization_id: organizationId })
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      if (error) throw error;

      const newCurrentOrg = organizations.find(org => org.id === organizationId);
      setCurrentOrganization(newCurrentOrg || null);

      toast({
        title: "Organisation changée",
        description: `Vous êtes maintenant dans ${newCurrentOrg?.name}`
      });

      // Invalidate relevant queries instead of reloading
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
      await queryClient.invalidateQueries({ queryKey: ['user-profile'] });

    } catch (error) {
      console.error('Error switching organization:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de changer d'organisation"
      });
    }
  };

  const createOrganization = async (orgData: {
    name: string;
    slug: string;
    description?: string;
    plan_type?: string;
  }) => {
    try {
      const { data: newOrg, error } = await supabase
        .from('organizations')
        .insert([{
          ...orgData,
          plan_type: orgData.plan_type || 'basic'
        }])
        .select()
        .single();

      if (error) throw error;

      // Ajouter l'utilisateur comme owner
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert([{
          organization_id: newOrg.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          role: 'owner',
          joined_at: new Date().toISOString()
        }]);

      if (memberError) throw memberError;

      toast({
        title: "Organisation créée",
        description: `${newOrg.name} a été créée avec succès`
      });

      await fetchUserOrganizations();
      return newOrg;

    } catch (error) {
      console.error('Error creating organization:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de créer l'organisation"
      });
      throw error;
    }
  };

  const inviteMember = async (organizationId: string, email: string, role: string = 'member') => {
    try {
      // Pour l'instant, on simule une invitation en créant directement le membre
      // Dans un vrai système, on enverrait un email d'invitation
      const { error } = await supabase
        .from('organization_members')
        .insert([{
          organization_id: organizationId,
          user_id: null, // On mettra l'ID utilisateur quand il acceptera l'invitation
          role,
          invited_at: new Date().toISOString()
        }]);

      if (error) throw error;

      toast({
        title: "Invitation envoyée",
        description: `Une invitation a été envoyée à ${email}`
      });

    } catch (error) {
      console.error('Error inviting member:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'envoyer l'invitation"
      });
    }
  };

  useEffect(() => {
    fetchUserOrganizations();
  }, []);

  return {
    organizations,
    currentOrganization,
    userMemberships,
    loading,
    switchOrganization,
    createOrganization,
    inviteMember,
    refreshOrganizations: fetchUserOrganizations
  };
}